import type { AppProps } from 'next/app';
import { NextIntlClientProvider } from 'next-intl';
import { useRouter } from 'next/router';
import '@/styles/main.scss';
import { Poppins } from 'next/font/google';
import Head from 'next/head';
import MainLayout from '@/layout/main-layout/main-layout';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useListenerCheck } from '@/hooks/listeners/use-listener-check';
import dynamic from 'next/dynamic';
import Script from 'next/script';

const CheckOnMain = dynamic(
  () =>
    import('@/shared/components/check-on-main/check-on-main').then(
      (mod) => mod.default
    ),
  {
    ssr: false,
  }
);

const poppins = Poppins({
  weight: ['400', '500', '600', '700'],
  subsets: ['latin'],
  display: 'swap',
  fallback: ['sans-serif'],
});

const queryClient = new QueryClient();

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const { timeZone = '' } = Intl.DateTimeFormat().resolvedOptions();
  const checkID = useListenerCheck();

  return (
    <>
      <Head>
        {(process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' ||
          router.pathname === '/check-result/[id]') && (
          <meta name="robots" content="noindex, nofollow" />
        )}

        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <style
          dangerouslySetInnerHTML={{
            __html: `
                :root {
                  --fontFamily: ${poppins.style.fontFamily};
                }
          `,
          }}
        />
      </Head>

      <NextIntlClientProvider
        locale={router.locale}
        messages={pageProps.messages}
        timeZone={timeZone}
      >
        <QueryClientProvider client={queryClient}>
          <MainLayout lazyFooter={pageProps.lazyFooter} isCheckPage={!!checkID}>
            {checkID ? (
              <CheckOnMain checkID={checkID} />
            ) : (
              <Component {...pageProps} />
            )}
          </MainLayout>
        </QueryClientProvider>
      </NextIntlClientProvider>

      {process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' && (
        <Script
          id="msc_clarity"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
              (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "${process.env.NEXT_PUBLIC_MS_CLARITY}");
          `,
          }}
        />
      )}
    </>
  );
}
